import { useState, useEffect, useMemo } from 'react';
import { mcGetTokenOhlcv } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Activity, BarChart3, TrendingUp } from 'lucide-react';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { fmtUsd, fmtPrice } from '../lib/numfmt';

// Period → GeckoTerminal OHLCV timeframe (limit 120 candles).
const PERIODS = [
  { label: '24H', tf: '15m' },
  { label: '7D', tf: '1h' },
  { label: '30D', tf: '4h' },
  { label: '90D', tf: '1d' },
];
const TYPES = [
  { key: 'volume', label: 'Volume', icon: BarChart3 },
  { key: 'price', label: 'Price', icon: TrendingUp },
];

function VolTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-card border border-border rounded p-2.5 text-xs shadow-xl">
      <p className="text-muted-foreground mb-1">{new Date(d.ts * 1000).toLocaleString()}</p>
      <p className="text-blue-400">Volume: <span className="font-mono font-semibold">{fmtUsd(d.v)}</span></p>
      <p className="text-amber-400">Close: <span className="font-mono font-semibold">{fmtPrice(d.c)}</span></p>
    </div>
  );
}

export default function McVolumeHistory({ chain, token, pool }) {
  const [period, setPeriod] = useState('7D');
  const [type, setType] = useState('volume');
  const [candles, setCandles] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const tf = PERIODS.find((p) => p.label === period)?.tf || '1h';
    mcGetTokenOhlcv(chain, token, tf, pool || undefined)
      .then((d) => { if (alive) setCandles(d?.candles || []); })
      .catch(() => { if (alive) setCandles([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [chain, token, pool, period]);

  const summary = useMemo(() => {
    const cs = candles || [];
    if (cs.length === 0) return null;
    return {
      totalVol: cs.reduce((s, c) => s + (c.v || 0), 0),
      count: cs.length,
      high: Math.max(...cs.map((c) => c.h)),
      low: Math.min(...cs.map((c) => c.l)),
    };
  }, [candles]);

  const fmtX = (ts) => {
    const d = new Date(ts * 1000);
    return period === '24H'
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <Card data-testid="token-volume-history">
      <CardContent className="pt-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Activity className="w-4 h-4 text-primary" /> Volume &amp; Price History
            <span className="text-[10px] text-muted-foreground font-normal">from pool OHLCV</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="flex rounded border border-border overflow-hidden">
              {PERIODS.map((p) => (
                <button
                  key={p.label}
                  data-testid={`vol-hist-period-${p.label}`}
                  onClick={() => setPeriod(p.label)}
                  className={`px-2.5 py-1 text-[10px] font-medium transition-colors ${period === p.label ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex rounded border border-border overflow-hidden">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  data-testid={`vol-hist-type-${t.key}`}
                  onClick={() => setType(t.key)}
                  className={`px-2.5 py-1 text-[10px] font-medium transition-colors flex items-center gap-1 ${type === t.key ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}
                >
                  <t.icon className="w-3 h-3" /> {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : !candles || candles.length < 2 ? (
          <div className="h-64 flex items-center justify-center text-sm text-muted-foreground" data-testid="vol-hist-empty">
            No OHLCV history for this pool / period
          </div>
        ) : (
          <>
            {summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="border border-border p-2.5">
                  <div className="mono-label text-[10px]">Total Volume</div>
                  <div className="text-sm font-mono font-bold text-blue-400 mt-0.5">{fmtUsd(summary.totalVol)}</div>
                </div>
                <div className="border border-border p-2.5">
                  <div className="mono-label text-[10px]">Candles</div>
                  <div className="text-sm font-mono font-bold mt-0.5">{summary.count}</div>
                </div>
                <div className="border border-border p-2.5">
                  <div className="mono-label text-[10px]">Period High</div>
                  <div className="text-sm font-mono font-bold text-emerald-500 mt-0.5">{fmtPrice(summary.high)}</div>
                </div>
                <div className="border border-border p-2.5">
                  <div className="mono-label text-[10px]">Period Low</div>
                  <div className="text-sm font-mono font-bold text-red-500 mt-0.5">{fmtPrice(summary.low)}</div>
                </div>
              </div>
            )}
            <div className="h-72" data-testid="vol-hist-chart">
              <ResponsiveContainer width="100%" height="100%">
                {type === 'volume' ? (
                  <BarChart data={candles}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.12)" />
                    <XAxis dataKey="ts" tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={fmtX} minTickGap={28} />
                    <YAxis tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => fmtUsd(v)} width={54} />
                    <Tooltip content={<VolTooltip />} cursor={{ fill: 'rgba(128,128,128,0.08)' }} />
                    <Bar dataKey="v" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                  </BarChart>
                ) : (
                  <AreaChart data={candles}>
                    <defs>
                      <linearGradient id="mcPriceGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.12)" />
                    <XAxis dataKey="ts" tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={fmtX} minTickGap={28} />
                    <YAxis tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => fmtPrice(v)} domain={['dataMin', 'dataMax']} width={64} />
                    <Tooltip content={<VolTooltip />} />
                    <Area type="monotone" dataKey="c" stroke="#f59e0b" fill="url(#mcPriceGrad)" strokeWidth={2} dot={false} />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
